import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Gem,
  Star,
  Users,
  Scissors,
  Crown,
  Sparkles,
  ArrowRight,
  Calendar,
} from "lucide-react";
import { GALLERY } from "@/data/menzy";

const strip = [
  { Icon: Scissors, title: "Haircuts", sub: "All styles" },
  { Icon: Sparkles, title: "Beard Care", sub: "Line ups & grooming" },
  { Icon: Crown, title: "Premium Styles", sub: "Tapers • Fades • Waves" },
  { Icon: Star, title: "Clean Environment", sub: "Professional & Comfortable" },
];

const benefits = [
  { Icon: Gem, title: "Premium Haircuts" },
  { Icon: Star, title: "Attention to Detail" },
  { Icon: Users, title: "All Hair Types Welcome" },
];

const CHAIR_BG =
  "https://images.pexels.com/photos/8867166/pexels-photo-8867166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const HERO_VIDEOS = ["/assets/v6.mp4", "/assets/v7.mp4", "/assets/v8.mp4"];

export default function Home() {
  const navigate = useNavigate();
  const goBook = () => navigate("/bookings");
  const videoRef = useRef(null);
  const [vidIndex, setVidIndex] = useState(0);
  const nextVideo = () => setVidIndex((i) => (i + 1) % HERO_VIDEOS.length);

  return (
    <div className="bg-[#09090b] text-white">
      {/* ===== HERO (full-bleed banner) ===== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        {/* barber image anchored right, full height */}
        <div className="absolute inset-0">
          {/* barber video header — seamless cycle through clips */}
          <video
            key={vidIndex}
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onEnded={nextVideo}
            poster="/assets/menzy_portrait.png"
            className="absolute inset-0 w-full h-full object-cover object-center"
          >
            <source src={HERO_VIDEOS[vidIndex]} type="video/mp4" />
          </video>

          {/* boosted dark overlays so the headline pops */}
          <div className="absolute inset-0 bg-[#09090b]/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] from-15% via-[#09090b]/82 sm:via-[#09090b]/62 lg:via-[#09090b]/38 to-[#09090b]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/55" />
        </div>

        {/* left copy */}
        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-6 pt-24 sm:pt-28 pb-14">
          <div className="max-w-xl">
            <div
              className="animate-fade-up space-y-1 text-lg sm:text-2xl tracking-[0.32em] text-white font-semibold"
              style={{ animationDelay: "0.05s" }}
            >
              <p>LOOK GOOD</p>
              <p>FEEL BETTER</p>
              <p>BE YOU</p>
            </div>

            <img
              src="/assets/logo_full.png"
              alt="MENZY — Master Barber"
              className="animate-fade-up mt-4 w-[90%] max-w-[560px] lg:max-w-[640px] h-auto"
              style={{ animationDelay: "0.12s" }}
            />

            <p
              className="animate-fade-up mt-4 text-base sm:text-xl tracking-[0.2em] text-white font-semibold uppercase"
              style={{ animationDelay: "0.28s" }}
            >
              Clean Cuts. Confident You.
            </p>

            {/* benefit indicators */}
            <div className="animate-fade-up mt-8 flex items-stretch gap-4 sm:gap-7" style={{ animationDelay: "0.36s" }}>
              {benefits.map(({ Icon, title }, i) => (
                <div
                  key={title}
                  className={`flex flex-col items-center text-center gap-2 ${i !== 0 ? "border-l border-[#d4af37]/30 pl-4 sm:pl-7" : ""}`}
                >
                  <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#f4d160]" strokeWidth={1.5} />
                  <span className="text-[10px] sm:text-xs uppercase tracking-widest text-white/90 font-semibold leading-tight max-w-[96px]">
                    {title}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={goBook}
              data-testid="hero-book-appointment-button"
              className="animate-fade-up mt-9 gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-sm sm:text-base px-8 py-4 rounded-full flex items-center gap-3 shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-transform duration-300 hover:scale-[1.03]"
              style={{ animationDelay: "0.44s" }}
            >
              Book Your Appointment
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* quote bottom-right */}
        <div className="hidden lg:block absolute z-10 right-10 xl:right-16 bottom-20 text-right max-w-[260px]">
          <p className="text-sm xl:text-base font-bold uppercase tracking-wide text-white leading-snug">
            "A good haircut
            <br />
            changes everything."
          </p>
          <p className="mt-2 text-xs tracking-[0.35em] text-[#f4d160] font-semibold">— MENZY</p>
        </div>
      </section>

      {/* ===== SPECIALTY STRIP ===== */}
      <section className="border-y border-[#d4af37]/15 bg-[#0c0c0f]">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4">
          {strip.map(({ Icon, title, sub }, i) => (
            <div key={title} className={`flex items-center gap-3 ${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-6" : ""}`}>
              <Icon className="h-7 w-7 text-[#f4d160] shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-bold uppercase tracking-wide">{title}</p>
                <p className="text-xs text-white/55">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== RECENT WORK GALLERY ===== */}
      <section id="work" className="py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <div className="flex items-end justify-between flex-wrap gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-semibold">Recent Work</p>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-[2.9rem] leading-tight flex flex-wrap items-center gap-x-4">
                <span>REAL CUTS.</span>
                <span>REAL CLIENTS.</span>
                <span>REAL RESULTS.</span>
                <span className="hidden sm:inline-block h-[3px] w-14 gold-surface rounded-full" />
              </h2>
              <p className="mt-3 text-white/60">Precision in every cut. Quality in every detail.</p>
            </div>
            <button
              onClick={goBook}
              data-testid="gallery-book-button"
              className="gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-sm px-6 py-3.5 rounded-full flex items-center gap-2 shadow-[0_0_22px_rgba(212,175,55,0.4)] transition-transform duration-300 hover:scale-[1.03]"
            >
              <Calendar className="h-4 w-4" strokeWidth={2.5} /> Book Now
            </button>
          </div>

          <div className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {GALLERY.map(({ title, src }) => (
              <div key={title} data-testid={`gallery-item-${title}`} className="group relative overflow-hidden rounded-2xl border border-[#26262d]">
                <img src={src} alt={title} className="w-full h-56 sm:h-80 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-4 text-center">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-white">{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOOK YOUR CUT TODAY ===== */}
      <section className="relative overflow-hidden border-y border-[#d4af37]/20">
        <div className="absolute inset-0">
          <img src={CHAIR_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/85 to-[#09090b]/70" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 py-14 sm:py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-semibold">Appointments</p>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl">Book Your Cut Today</h2>
            <p className="mt-3 text-white/70 leading-relaxed">
              Secure your spot with Menzy (Master Barber).
              <br className="hidden sm:block" /> Simple. Fast. Easy.
            </p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-3">
            <button
              onClick={goBook}
              data-testid="bottom-cta-book-button"
              className="gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-sm sm:text-base px-8 py-4 rounded-full inline-flex items-center gap-3 shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-transform duration-300 hover:scale-[1.03]"
            >
              <Calendar className="h-5 w-5" strokeWidth={2.5} /> Book Now <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <p className="text-xs tracking-[0.3em] uppercase text-[#d4af37]/80 font-semibold">Look Good. Feel Better. Be You.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
